import React, { useEffect, useState } from "react";
import ContentContainer from "../components/ContentContainer.tsx";
import {
  Button,
  Checkbox,
  FileAttachment,
  FormColumn2Wide,
  FormColumn4Wide,
  Input,
  isValidEmail,
  Paper,
  Select,
  Typography,
  useAlerts,
  useLoading,
  ValueOpt
} from "best-common-react";
import Papa from "papaparse";
import EmailListUploadTable from "../components/EmailListUploadTable.tsx";
import {
  EmailListData,
  EmailListMetadata,
  EmailListUploadDTO,
  FileUpload,
  InvalidEmailListRow
} from "../types/emailList.ts";
import styled from "styled-components";
import { ListUploadTypeOptions } from "../constants/DropdownsConstants.ts";
import { useMetadata } from "../contexts/MetadataContext.tsx";
import { SourceCode } from "../types/metadata.ts";
import { BullseyeQuery } from "../types/query.ts";
import { getQueries, uploadEmailList } from "../api/AuthenticatedClient.ts";
import { useDropdowns } from "../contexts/DropdownsContext.tsx";
import InvalidEmailListUploadModal from "../components/InvalidEmailListUploadModal.tsx";
import BullseyeSubSection from "../components/BullseyeSubSection.tsx";

const ACCEPTED_FILE_TYPES = ["text/csv"];

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-bottom: 1rem;
  margin-top: 1rem;
`;

const LabelContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
`;

const Label = styled.span``;

const SelectAllButton = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const UploadEmailList: React.FC = () => {
  const { sourceCodes } = useMetadata();
  const { teamOptions } = useDropdowns();
  const { setLoading } = useLoading();
  const { addAlert } = useAlerts();
  const [metadata, setMetadata] = useState<EmailListMetadata>({
    optInFlag: false,
    reOptInFlag: false,
    sendToEspFlag: true,
    addInternalsFlag: true,
  });
  const [containsHeadersFlag, setContainsHeadersFlag] = React.useState<boolean>(true);
  const [file, setFile] = useState<FileUpload>();
  const [headers, setHeaders] = useState<(keyof EmailListData)[]>([]);

  // Validation
  const [canSave, setCanSave] = useState(false);
  const [invalidRows, setInvalidRows] = useState<InvalidEmailListRow[]>([]);
  const [showInvalidEmailListModal, setShowInvalidEmailListModal] = useState(false);

  // Dropdown Options
  const [sourceCodeOptions, setSourceCodeOptions] = React.useState<ValueOpt<SourceCode>[]>([]);
  const [bullseyeQueries, setBullseyeQueries] = useState<BullseyeQuery[]>([]);
  const [queryOptions, setQueryOptions] = useState<ValueOpt<BullseyeQuery>[]>([]);
  const [optOutQueryOptions, setOptOutQueryOptions] = React.useState<ValueOpt<BullseyeQuery>[]>();

  useEffect(() => {
    getQueries().then(setBullseyeQueries).catch(console.error);
  }, []);

  useEffect(() => {
    if (bullseyeQueries && metadata.teamId) {
      const filtered = bullseyeQueries.filter(({ teamId }) => teamId === metadata.teamId);
      setQueryOptions(filtered.map(query => ({ label: query.name, value: query })));
    } else {
      setQueryOptions([]);
    }
  }, [bullseyeQueries, metadata.teamId]);

  useEffect(() => {
    if (!metadata.optInFlag) {
      setOptOutQueryOptions(queryOptions);
    } else {
      setOptOutQueryOptions([]);
    }

  }, [metadata.optInFlag, queryOptions])

  useEffect(() => {
    if (sourceCodes && metadata.teamId) {
      const filtered = sourceCodes.filter(
        ({ sourceCode, teamId }) => teamId === metadata.teamId || sourceCode === 500 || sourceCode === 903
      );
      setSourceCodeOptions(filtered.map(sc => ({ label: `${sc.description} (${sc.sourceCode})`, value: sc })));
    } else {
      setSourceCodeOptions([]);
    }
  }, [sourceCodes, metadata.teamId]);

  useEffect(() => {
    const validMetadata =
      !!metadata.name?.length && !!metadata.uploadType && !!metadata.teamId && !!metadata.sourceCode;
    const validOptIn = metadata.uploadType === "Commercial" ? (!metadata.optInFlag || !!metadata.optInQueryIds?.length) : true;
    const validOptOut = metadata.uploadType === "Commercial" ? (!metadata.sendToEspFlag || !!metadata.optOutQueryId) : true;
    const validHeaders =
      headers.length === file?.numColumns &&
      !headers.find(header => !header) &&
      !!headers.find(header => header === "email");

    setCanSave(validMetadata && validOptIn && validOptOut && !!file && validHeaders);
  }, [metadata, file, headers]);

  const updateQueryIds = (selectedIds: number[]) => {
    let optOutQueryId = metadata.optOutQueryId;
    if (optOutQueryId && !selectedIds?.includes(optOutQueryId)) {
      optOutQueryId = undefined;
    }
    setMetadata({ ...metadata, optInQueryIds: selectedIds, optOutQueryId });
  };

  const handleMetadataChange = (key: keyof EmailListMetadata, value: unknown) => {
    setMetadata({ ...metadata, [key]: value });
  };

  const onListUploadTypeChange = (value: string) => {
    if (value === "Service") {
      setMetadata({
        ...metadata,
        uploadType: value,
        optInFlag: false,
        reOptInFlag: false,
        optInLanguage: undefined,
        optInQueryIds: undefined,
        optOutQueryId: undefined,
      });
    } else {
      setMetadata({ ...metadata, uploadType: value });
    }
  }

  const uploadFiles = async (files: File[]) => {
    Papa.parse(files[0], {
      header: false,
      complete: results => {
        // @ts-expect-error
        const fileData: string[][] = results.data;
        const columns = fileData[0].length;
        const rows = containsHeadersFlag ? fileData.length - 1 : fileData.length;

        setFile({
          numColumns: columns,
          numRows: rows,
          headers: containsHeadersFlag ? fileData[0] : undefined,
          data: containsHeadersFlag ? fileData.slice(1) : fileData
        });
        setMetadata({ ...metadata, rows, columns });
      }
    });
  };

  const processFile = (): EmailListData[] => {
    if (!file || headers.length !== file.numColumns) {
      console.error("Error processing file.");
      throw new Error("Error processing file.");
    }

    return file.data.map(row => {
      const obj: EmailListData = {};
      headers.forEach((header, index) => {
        obj[header] = header === "email" ? row[index]?.trim() : row[index];
      });
      return obj;
    });
  };

  const clearFile = () => {
    setContainsHeadersFlag(true);
    setFile(undefined);
    setHeaders([]);
  };

  const resetForm = () => {
    setMetadata({
      name: undefined,
      uploadType: undefined,
      teamId: undefined,
      sourceCode: undefined,

      rows: undefined,
      columns: undefined,

      fields: undefined,
      uploadHeaders: undefined,

      optInFlag: false,
      reOptInFlag: false,
      optInQueryIds: undefined,

      sendToEspFlag: true,
      optOutQueryId: undefined,

      addInternalsFlag: true
    });
    clearFile();
  };

  const onSave = async () => {
    setLoading(true);
    try {
      if (file) {
        const body: EmailListUploadDTO = {
          metadata: {
            ...metadata,
            columns: file.numColumns,
            rows: file.numRows,
            fields: headers,
            uploadHeaders: !!file.headers ? file.headers : [...Array(file.numColumns)].map((_, i) => `${i}`)
          },
          data: processFile()
        };
        await uploadEmailList(body);
        resetForm();
        addAlert({
          type: "success",
          text: "Successfully Uploaded Email List"
        });
      }
    } catch (e) {
      console.error(e);
      addAlert({
        type: "danger",
        text: "Error Uploading Email List"
      });
    } finally {
      setLoading(false);
    }
  };

  const validateEmailAddresses = (colIdx: number) => {
    if (!file) return;

    const invalidEmailAddresses: InvalidEmailListRow[] = [];
    const emailAddresses: string[] = file.data.map(row => row[colIdx]);

    emailAddresses.forEach((emailAddress, idx) => {
      if (!isValidEmail(emailAddress?.trim())) {
        invalidEmailAddresses.push({
          row: idx + 1,
          email: emailAddress
        });
      }
    });

    if (invalidEmailAddresses.length) {
      setInvalidRows(invalidEmailAddresses);
      setShowInvalidEmailListModal(true);
    }
  };

  const onOmitInvalidEmailAddresses = () => {
    if (!file || !invalidRows.length) return;
    setMetadata({ ...metadata, rows: file.numRows - invalidRows.length });
    setFile({
      ...file,
      numRows: file.numRows - invalidRows.length,
      data: file.data.filter((_, idx) => !invalidRows.find(row => row.row === idx + 1))
    });
    setInvalidRows([]);
    setShowInvalidEmailListModal(false);
  };

  const onInvalidUploadClose = () => {
    const index = headers.findIndex(value => value === "email");
    const temp = [...headers];
    // @ts-ignore
    temp[index] = undefined;
    setHeaders(temp);

    setShowInvalidEmailListModal(false);
  };

  return (
    <>
      <InvalidEmailListUploadModal
        open={showInvalidEmailListModal}
        invalidEmailAddress={invalidRows}
        onOmitRows={onOmitInvalidEmailAddresses}
        onClose={onInvalidUploadClose}
      />
      <ContentContainer>
        <Typography variant="h5">Upload Email List</Typography>

        <Paper>
          <BullseyeSubSection header="About Your Upload">
            <div className="row">
              <FormColumn4Wide>
                <Input
                  id="email-list-name"
                  label="Upload Name"
                  value={metadata.name}
                  onChange={value => {
                    handleMetadataChange("name", value);
                  }}
                />
              </FormColumn4Wide>
              <FormColumn4Wide>
                <Select
                  id="list-upload-type-select"
                  label="List Type"
                  value={ListUploadTypeOptions.find(option => option.value === metadata.uploadType)}
                  options={ListUploadTypeOptions}
                  onChange={option => {
                    onListUploadTypeChange(option?.value);
                  }}
                />
              </FormColumn4Wide>
              <FormColumn4Wide>
                <Select
                  id="team-select"
                  label="Team"
                  value={teamOptions.find(option => option.value.id === metadata.teamId)}
                  options={teamOptions}
                  onChange={option => {
                    if (metadata.teamId !== option?.value.id) {
                      setMetadata({
                        ...metadata,
                        teamId: option?.value.id,
                        sourceCode:
                          metadata.sourceCode === 500 || metadata.sourceCode === 903 ? metadata.sourceCode : undefined,
                        optInQueryIds: undefined,
                        optOutQueryId: undefined
                      });
                    }
                  }}
                />
              </FormColumn4Wide>
              <FormColumn4Wide>
                <Select
                  id="source-code-select"
                  label="Source Code"
                  value={sourceCodeOptions.find(option => option.value.sourceCode === metadata.sourceCode)}
                  options={sourceCodeOptions}
                  onChange={option => {
                    handleMetadataChange("sourceCode", option?.value.sourceCode);
                  }}
                  disabled={!metadata.teamId}
                />
              </FormColumn4Wide>
            </div>
          </BullseyeSubSection>

          {metadata.uploadType === "Commercial" && (
            <BullseyeSubSection header="Opt In and Customer Creation">
                <div className="row">
                  <FormColumn4Wide>
                    <Checkbox
                      id="opt-in-flag-checkbox"
                      label="Opt In?"
                      checked={metadata.optInFlag}
                      onChange={checked => {
                        setMetadata({
                          ...metadata,
                          optInFlag: checked,
                          reOptInFlag: checked ? metadata.reOptInFlag : false,
                          optInQueryIds: checked ? metadata.optInQueryIds : undefined,
                          optOutQueryId: undefined
                        });
                      }}
                    />
                  </FormColumn4Wide>
                  {metadata.optInFlag && (
                    <>
                      <FormColumn4Wide>
                        <Checkbox
                          id="re-opt-in-flag-checkbox"
                          label="Re-Opt In?"
                          checked={metadata.reOptInFlag}
                          onChange={checked => {
                            handleMetadataChange("reOptInFlag", checked);
                          }}
                        />
                      </FormColumn4Wide>
                      <FormColumn4Wide>
                        <Input
                          id="opt-in-language-input"
                          label="Opt In Language"
                          value={metadata.optInLanguage}
                          onChange={value => {
                            handleMetadataChange("optInLanguage", value);
                          }}
                        />
                      </FormColumn4Wide>
                      <FormColumn4Wide>
                        <LabelContainer>
                          <Label>Opt In Query IDs</Label>
                          <SelectAllButton
                            onClick={() => {
                              if (!!metadata.teamId) {
                                const selectedIds = queryOptions.map(option => option.value.id);
                                updateQueryIds(selectedIds);
                                setOptOutQueryOptions(queryOptions);
                              }
                            }}
                          >
                            Select All
                          </SelectAllButton>
                        </LabelContainer>
                        <Select
                          id="opt-in-query-ids-select"
                          label=""
                          value={queryOptions.filter(option => metadata.optInQueryIds?.includes(option.value.id))}
                          options={queryOptions}
                          onChange={(value?: ValueOpt<BullseyeQuery> | ValueOpt<BullseyeQuery>[]) => {
                            const selectedIds = (value as ValueOpt<BullseyeQuery>[])?.map(option => option.value.id);
                            updateQueryIds(selectedIds);
                            setOptOutQueryOptions(value as ValueOpt<BullseyeQuery>[]);
                          }}
                          isMulti
                          disabled={!metadata.teamId}
                        />
                      </FormColumn4Wide>
                    </>
                  )}
                </div>
              </BullseyeSubSection>
              )}

        {!!metadata.uploadType && (
          <BullseyeSubSection header="Email">
            <div className="row ms-2">
              <FormColumn2Wide>
                <Checkbox
                  id="send-to-esp-checkbox"
                  label="Send to Email Service Provider?"
                  checked={metadata.sendToEspFlag}
                  onChange={checked => {
                    setMetadata({ ...metadata, sendToEspFlag: checked, optOutQueryId: undefined });
                  }}
                />
              </FormColumn2Wide>
              {metadata.uploadType === "Commercial" && metadata.sendToEspFlag && (
                <FormColumn2Wide>
                  <Select
                    id="opt-out-query-id-select"
                    label="Opt Out Query ID"
                    value={optOutQueryOptions?.find(option => option.value.id === metadata.optOutQueryId)}
                    options={optOutQueryOptions || []}
                    onChange={value => {
                      handleMetadataChange("optOutQueryId", value?.value.id);
                    }}
                    disabled={!metadata.teamId}
                  />
                </FormColumn2Wide>
              )}
            </div>
          </BullseyeSubSection>
          )}

          <BullseyeSubSection header="Upload Your List">
            {!file ? (
              <div>
                <div className="d-flex mb-2">
                  <Checkbox
                    id="add-internals-checkbox"
                    className="me-4"
                    label="Add internals"
                    checked={metadata.addInternalsFlag}
                    onChange={checked => {
                      handleMetadataChange("addInternalsFlag", checked);
                    }}
                  />
                  <Checkbox
                    id="upload-contains-headers-checkbox"
                    label="Upload has headers"
                    checked={containsHeadersFlag}
                    onChange={setContainsHeadersFlag}
                  />
                </div>

                <FileAttachment
                  id="prospect-tpp-consent-form"
                  className="w-100"
                  placeholder="Drop files here."
                  fileTypes={ACCEPTED_FILE_TYPES}
                  maxFiles={10}
                  files={[]}
                  onChange={(files: File[]) => {
                    uploadFiles(files).catch(console.error);
                  }}
                  showTable={false}
                  browseButtonText={"Browse"}
                  disabled={false}
                />
              </div>
            ) : (
              <div>
                <div className="w-100 mb-3">
                  <Button className="mb-1" variant="primary" onClick={clearFile}>
                    New Upload
                  </Button>
                  <Typography variant="div" style={{ fontStyle: "italic" }}>
                    This upload contains {file.numRows} rows. Use the sample below to map your headers:
                  </Typography>
                </div>
                <EmailListUploadTable
                  file={file}
                  headers={headers}
                  setHeaders={setHeaders}
                  validateEmailAddresses={validateEmailAddresses}
                />
              </div>
            )}
          </BullseyeSubSection>
          <Footer>
            <Button variant="primary" onClick={onSave} disabled={!canSave}>
              Upload
            </Button>
          </Footer>
        </Paper>
      </ContentContainer>
    </>
  );
};

export default UploadEmailList;
